import { FormEvent, useCallback, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { clearRefreshUnavailable404 } from '../api/authRefresh';
import { persistStoredRefreshToken } from '../auth/sessionToken';
import { useI18n } from '../i18n';
import { useAppNavigation } from '../navigation/AppNavigationContext';
import { persistIdentifierForLoginOption } from './lastLoginStorage';
import { persistLastSignInMethod } from './lastSignInMethod';
import { seedProfileFromLiteLogin } from './profileLoginSync';
import { serverRoleMatchesLoginType } from './authEntryPolicy';
import { roleIdFromJwt, profileRoleIdFromData } from '../auth/sessionBookingRole';
import { translateLoginOption } from '../i18n';
import { formatPhoneForDisplay, formatIsraeliPhoneInput } from '../utils/phoneE164';
import { localizeOtpFailureMessage } from '../i18n/localizeServerMessage';
import {
  isValidOtpPhone,
  normalizeOtpCode,
  normalizePhoneForOtpWire,
  sendPhoneOtpCode,
  verifyPhoneOtpCode,
  friendlyPhoneOtpSendFailureSummary,
} from './phoneOtpAuth';
import { loadPersistedOtpLoginAttempt, persistOtpLoginAttempt } from '../utils/authAttemptStorage';
import type { LoginOption } from './loginConstants';

export type PhoneOtpStep = 'phone' | 'code';

export function usePhoneOtpLogin(loginType: LoginOption = 'private client') {
  const { t } = useI18n();
  const { applyLoginSuccess, isLoggedIn } = useAuth();
  const { setView } = useAppNavigation();
  const [step, setStep] = useState<PhoneOtpStep>('phone');
  const [phone, setPhone] = useState(() => {
    const stored = loadPersistedOtpLoginAttempt(loginType)?.phone ?? '';
    return stored ? formatPhoneForDisplay(stored) : '';
  });
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorFriendly, setErrorFriendly] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<string | null>(null);
  const [codeSent, setCodeSent] = useState(false);

  const clearErrors = useCallback(() => {
    setErrorFriendly(null);
    setErrorDetails(null);
  }, []);

  const onChangePhone = useCallback(
    (value: string) => {
      setPhone(formatIsraeliPhoneInput(value));
      clearErrors();
    },
    [clearErrors],
  );

  const onChangeCode = useCallback(
    (value: string) => {
      setCode(value);
      clearErrors();
    },
    [clearErrors],
  );

  const onBackToPhone = useCallback(() => {
    setStep('phone');
    setCode('');
    setCodeSent(false);
    clearErrors();
  }, [clearErrors]);

  const onSendCode = useCallback(async () => {
    if (isLoggedIn || submitting) {
      return;
    }
    if (!isValidOtpPhone(phone)) {
      setErrorFriendly(t('auth.portal.invalidPhone'));
      setErrorDetails(null);
      return;
    }

    const wirePhone = normalizePhoneForOtpWire(phone);
    setSubmitting(true);
    clearErrors();
    persistOtpLoginAttempt(loginType, { phone: wirePhone });
    try {
      const result = await sendPhoneOtpCode(wirePhone, loginType);
      if (result.ok) {
        setPhone(formatPhoneForDisplay(wirePhone));
        setStep('code');
        setCodeSent(true);
        return;
      }
      setErrorFriendly(
        localizeOtpFailureMessage(
          friendlyPhoneOtpSendFailureSummary(result.friendly, {
            apiNotDeployedFlag: result.apiNotDeployed,
            apiNotDeployed: t('auth.portal.phoneOtpApiNotDeployed'),
            userNotRegistered: t('auth.portal.phoneOtpUserNotRegistered'),
          }),
          t,
          'phone',
        ),
      );
      setErrorDetails(result.details);
    } finally {
      setSubmitting(false);
    }
  }, [clearErrors, isLoggedIn, loginType, phone, submitting, t]);

  const onVerifyCode = useCallback(async () => {
    if (isLoggedIn || submitting) {
      return;
    }
    const wirePhone = normalizePhoneForOtpWire(phone);
    const trimmedCode = normalizeOtpCode(code);
    if (!isValidOtpPhone(phone)) {
      setErrorFriendly(t('auth.portal.invalidPhone'));
      setErrorDetails(null);
      onBackToPhone();
      return;
    }
    if (trimmedCode.length < 4) {
      setErrorFriendly(t('auth.portal.invalidSmsCode'));
      setErrorDetails(null);
      return;
    }

    setSubmitting(true);
    clearErrors();
    try {
      const result = await verifyPhoneOtpCode(wirePhone, trimmedCode, loginType);
      if (!result.ok) {
        setErrorFriendly(localizeOtpFailureMessage(result.friendly, t, 'phone'));
        setErrorDetails(result.details);
        return;
      }
      const tokenRoleId = roleIdFromJwt(result.accessToken) ?? profileRoleIdFromData(result.parsed);
      if (!serverRoleMatchesLoginType(loginType, tokenRoleId)) {
        setErrorFriendly(
          t('auth.portal.otpWrongServerRole', {
            roleId: String(tokenRoleId ?? '?'),
            type: translateLoginOption(loginType, t),
          }),
        );
        setErrorDetails(null);
        return;
      }
      persistIdentifierForLoginOption(loginType, wirePhone);
      persistLastSignInMethod('otp');
      applyLoginSuccess(result.parsed, result.accessToken, wirePhone, loginType);
      if (result.refreshToken) {
        persistStoredRefreshToken(result.refreshToken);
        clearRefreshUnavailable404();
      }
      void seedProfileFromLiteLogin({ phone: wirePhone });
      setCode('');
      setView('home', { replace: true });
    } finally {
      setSubmitting(false);
    }
  }, [
    applyLoginSuccess,
    clearErrors,
    code,
    isLoggedIn,
    loginType,
    onBackToPhone,
    phone,
    setView,
    submitting,
    t,
  ]);

  const onSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (step === 'phone') {
        void onSendCode();
        return;
      }
      void onVerifyCode();
    },
    [onSendCode, onVerifyCode, step],
  );

  const displayPhone = formatPhoneForDisplay(normalizePhoneForOtpWire(phone) || phone);

  return {
    step,
    phone,
    displayPhone,
    code,
    submitting,
    codeSent,
    errorFriendly,
    errorDetails,
    onChangePhone,
    onChangeCode,
    onBackToPhone,
    onSendCode,
    onVerifyCode,
    onSubmit,
    clearErrors,
  };
}
